import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, NavigateFunction } from 'react-router-dom';

export type NavigateWithSearch = ((path: string, config?: {
  newParams?: Record<string, string | null | undefined>;
}) => void) & {
  /**
   * The original navigate function
   */
  navigate: NavigateFunction;
};

/**
 * @returns a memoized function that navigates to a given path while preserving search params
 */
const useNavigateWithSearch = (): NavigateWithSearch => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Navigates to a given path while preserving search params
   * @param path The path to navigate to
   * @param options optional config object
   */
  const navigateWithSearch = useCallback((path: string, config?: {
    /**
     * Record of params to set/update. Use null or undefined value to remove a param
     */
    newParams?: Record<string, string | null | undefined>;
  }) => {
    const currentParams = new URLSearchParams(searchParams.toString());
    const currentHash = window.location.hash;

    // Apply new params if provided
    if (config?.newParams) {
      Object.entries(config.newParams).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          setSearchParams((params) => {
            params.delete(key);
            return params;
          });
        } else {
          setSearchParams((params) => {
            params.set(key, value);
            return params;
          });
        }
      });
    }

    navigate({
      pathname: path,
      search: currentParams.toString(),
      hash: currentHash,
    });
  }, [navigate, searchParams, setSearchParams]) as NavigateWithSearch;

  const memorizedNavigateWithSearch = useMemo(() => {
    // Attach the original navigate function
    navigateWithSearch.navigate = navigate;
    return navigateWithSearch;
  }, [navigateWithSearch, navigate]);

  return memorizedNavigateWithSearch;
};

export default useNavigateWithSearch;
